<script lang="ts" setup>
    import { OffsetGroup } from '~/data/OffsetGroup'
    import { OffsetItem } from '~/data/OffsetItem'

    const {
        image,
        content,
        faded = true,
        fit = 'cover',
    } = defineProps<{
        image?: Asset
        content: string
        faded?: boolean
        fit?: 'cover' | 'contain'
    }>()

    const titleOffset = new OffsetGroup(
        new OffsetItem(9, 4),
        new OffsetItem(3, 0),
    )

    const contentOffset = new OffsetGroup(
        new OffsetItem(2, 7),
        new OffsetItem(3, 2),
    )

    const pageHeader = ref()
    usePageHeaderAnimation(pageHeader as Ref<HTMLElement>)
</script>

<template>
    <div
        ref="pageHeader"
        class="page-header relative overflow-hidden"
        :class="{
            faded,
            'text-dark': !image,
            'text-header-foreground with-image mb-12 lg:mb-16 xl:mb-24':
                !!image,
        }"
    >
        <Asset
            v-if="image"
            :asset="image"
            :fit="fit"
            class="page-hero h-svh w-full opacity-0 sm:aspect-[7/5] sm:h-auto md:aspect-[8/5] lg:aspect-video"
            data-aos="fade"
        />

        <div
            class="layout-grid pointer-events-none absolute left-0 right-0 top-6 z-50 mt-20 uppercase transition md:mt-0 lg:fixed"
            :class="{ 'opacity-0': useNavOpen().value }"
        >
            <div
                class="title-area pointer-events-auto relative font-bold opacity-0 md:mr-20 md:text-xl lg:left-12 lg:order-last lg:mr-0 lg:text-base xl:left-0 xl:mr-2 xl:text-xl"
                :class="titleOffset.classes"
                data-aos="fade-up"
                data-aos-delay="200"
            >
                <slot />
            </div>
        </div>

        <div
            ref="introArea"
            class="page-intro-area inset-0 z-10 h-full py-6"
            :class="{ absolute: !!image }"
        >
            <div class="layout-grid mt-20 uppercase transition-colors md:mt-0">
                <div
                    ref="intro"
                    v-html="content"
                    class="page-intro raw-content mb-8 flex max-w-[600px] flex-col justify-center self-start pr-8 text-3xl opacity-0 sm:text-4xl lg:justify-start lg:text-4xl xl:pr-12 xl:text-5xl 2xl:pr-0"
                    :class="{
                        'pt-12 md:pt-16 lg:pt-0': $slots.default,
                        [contentOffset.classes]: true,
                    }"
                    data-aos="fade-up"
                    data-aos-delay="100"
                />
            </div>
        </div>
    </div>
</template>

<style>
    .page-header {
        text-shadow: 0 0 2em rgba(128, 128, 128, 0);
        --animation-range: 50vh;
        --feature-image-margin: 0;
        --feature-image-width: 100;
        --page-header-opacity: 1;
        --feature-image-small-width: 100%;

        @screen lg {
            --feature-image-small-width: 87.5%;
        }

        @screen xl {
            --feature-image-small-width: 68.75%;
        }
    }

    .page-intro-area {
        min-height: calc(50vh + 100px);
    }

    .page-header.faded figure {
        margin-inline: auto;
        aspect-ratio: 16/9;
    }

    body:not(.page-home) .page-hero {
        opacity: var(--page-header-opacity) !important;

        @screen lg {
            margin-top: calc(var(--feature-image-margin) * 1px);
            width: calc(var(--feature-image-width) * 1%);
        }
    }

    .pin-spacer {
        width: 100% !important;

        .page-intro-area {
            width: 100% !important;
            max-width: 100% !important;
        }
    }
</style>
